import { useState } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  LeadDetail,
  CreateLeadRequest,
  MarketingCategory,
} from "@shared-core/data/api-types";
import { ApiError } from "@shared-core/data/api/errors";
import { extractFieldErrors } from "@shared-core/data/validation";
import {
  INTEREST_OPTIONS,
  buildLeadAddressFromForm,
} from "@shared-core/domain/leads";
import { withHttpScheme } from "@shared-core/domain/url";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../primitives/dialog";
import { Input } from "../../primitives/input";
import { Label } from "../../primitives/label";
import { Button } from "../../primitives/button";
import { AlertTriangle } from "lucide-react";

type FieldErrorMap = Record<string, string>;

interface CreateLeadModalProps {
  onClose: () => void;
  onCreated: (lead: LeadDetail) => void;
}

export function CreateLeadModal({ onClose, onCreated }: CreateLeadModalProps) {
  const dataAdapter = useDataAdapter();
  const [companyName, setCompanyName] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [postcode, setPostcode] = useState("");
  const [country, setCountry] = useState("United Kingdom");
  const [phone, setPhone] = useState("");
  const [website, setWebsite] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactJobTitle, setContactJobTitle] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [referredBy, setReferredBy] = useState("");
  const [interestTags, setInterestTags] = useState<MarketingCategory[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrorMap>({});
  const [formError, setFormError] = useState<string | null>(null);

  const canSubmit = companyName.trim() !== "" && contactName.trim() !== "";

  const toggleTag = (tag: MarketingCategory) => {
    setInterestTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );
  };

  async function handleSubmit() {
    if (!canSubmit) return;
    setIsSubmitting(true);
    setFieldErrors({});
    setFormError(null);
    try {
      // Address is optional at creation (keeps logging a lead lightweight)
      // but structured, not free text, since it needs to be a genuinely
      // deliverable address by the time this lead converts to a real
      // Tharstern customer — Tharstern has no separate delivery address,
      // this one does double duty. Sent as whatever fields are filled in;
      // an incomplete address is legitimate here (see
      // `buildLeadAddressFromForm`) and any deliverability gap is the
      // backend's own validation to flag at convert-time.
      const address = buildLeadAddressFromForm({
        addressLine1,
        addressLine2,
        city,
        postcode,
        country,
      });
      const input: CreateLeadRequest = {
        company_name: companyName.trim(),
        address,
        phone: phone.trim() || null,
        website: website.trim() ? withHttpScheme(website.trim()) : null,
        interest_tags: interestTags,
        contact: {
          name: contactName.trim(),
          job_title: contactJobTitle.trim() || null,
          email: contactEmail.trim() || null,
          phone: contactPhone.trim() || null,
          referred_by: referredBy.trim() || null,
        },
      };
      const lead = await dataAdapter.createLead(input);
      onCreated(lead);
    } catch (error) {
      console.error("sales_leads.create.failed", {
        status: error instanceof ApiError ? error.status : undefined,
      });
      const errs = extractFieldErrors(error);
      if (errs.length > 0) {
        const map: FieldErrorMap = {};
        for (const fe of errs) {
          if (!(fe.field in map)) map[fe.field] = fe.message;
        }
        setFieldErrors(map);
        setFormError("Please fix the highlighted fields and try again.");
      } else {
        setFormError("Couldn't create this lead. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>New lead</DialogTitle>
          <DialogDescription>
            A not-yet-a-customer company you&apos;re starting to work.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
          {formError && (
            <div className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-800 p-3 text-sm text-red-700 dark:text-red-300">
              <AlertTriangle className="size-4 shrink-0 mt-0.5" />
              {formError}
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="lead-company">Company name</Label>
            <Input
              id="lead-company"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="e.g. Hepco Rail Ltd"
              aria-invalid={fieldErrors.company_name ? true : undefined}
              aria-describedby={
                fieldErrors.company_name ? "lead-company-error" : undefined
              }
            />
            {fieldErrors.company_name && (
              <p
                id="lead-company-error"
                className="text-sm text-red-600 dark:text-red-400"
              >
                {fieldErrors.company_name}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="lead-phone">Phone</Label>
              <Input
                id="lead-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="lead-website">Website</Label>
              <Input
                id="lead-website"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                placeholder="e.g. hepcorail.co.uk"
                aria-invalid={fieldErrors.website ? true : undefined}
                aria-describedby={
                  fieldErrors.website ? "lead-website-error" : undefined
                }
              />
              {fieldErrors.website && (
                <p
                  id="lead-website-error"
                  className="text-sm text-red-600 dark:text-red-400"
                >
                  {fieldErrors.website}
                </p>
              )}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="lead-address-line1">Address</Label>
            <Input
              id="lead-address-line1"
              value={addressLine1}
              onChange={(e) => setAddressLine1(e.target.value)}
              placeholder="Address line 1"
            />
            <Input
              value={addressLine2}
              onChange={(e) => setAddressLine2(e.target.value)}
              placeholder="Address line 2 (optional)"
            />
            <div className="grid grid-cols-3 gap-2">
              <Input
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="City"
                aria-label="City"
              />
              <Input
                value={postcode}
                onChange={(e) => setPostcode(e.target.value)}
                placeholder="Postcode"
                aria-label="Postcode"
              />
              <Input
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                placeholder="Country"
                aria-label="Country"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Interested in</Label>
            <div className="flex flex-wrap gap-2">
              {INTEREST_OPTIONS.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => toggleTag(tag)}
                  aria-pressed={interestTags.includes(tag)}
                  className={`px-3 py-1 rounded-full text-xs capitalize transition-colors border ${
                    interestTags.includes(tag)
                      ? "bg-primary text-white border-primary"
                      : "bg-surface border-border text-foreground hover:bg-surface-hover"
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          <div className="border-t border-border pt-4 space-y-4">
            <div className="text-sm font-medium text-foreground">
              First contact
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="lead-contact-name">Name</Label>
                <Input
                  id="lead-contact-name"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  aria-invalid={fieldErrors["contact.name"] ? true : undefined}
                  aria-describedby={
                    fieldErrors["contact.name"]
                      ? "lead-contact-name-error"
                      : undefined
                  }
                />
                {fieldErrors["contact.name"] && (
                  <p
                    id="lead-contact-name-error"
                    className="text-sm text-red-600 dark:text-red-400"
                  >
                    {fieldErrors["contact.name"]}
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="lead-contact-title">Job title</Label>
                <Input
                  id="lead-contact-title"
                  value={contactJobTitle}
                  onChange={(e) => setContactJobTitle(e.target.value)}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="lead-contact-email">Email</Label>
                <Input
                  id="lead-contact-email"
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  aria-invalid={fieldErrors["contact.email"] ? true : undefined}
                  aria-describedby={
                    fieldErrors["contact.email"]
                      ? "lead-contact-email-error"
                      : undefined
                  }
                />
                {fieldErrors["contact.email"] && (
                  <p
                    id="lead-contact-email-error"
                    className="text-sm text-red-600 dark:text-red-400"
                  >
                    {fieldErrors["contact.email"]}
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="lead-contact-phone">Phone</Label>
                <Input
                  id="lead-contact-phone"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="lead-referred-by">Referred by (optional)</Label>
              <Input
                id="lead-referred-by"
                value={referredBy}
                onChange={(e) => setReferredBy(e.target.value)}
                placeholder="e.g. an existing customer, a colleague"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleSubmit()}
            disabled={!canSubmit || isSubmitting}
          >
            {isSubmitting ? "Creating…" : "Create lead"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
